import { useEffect, useState } from "react";
import type { ComponentProps } from "react";
import { QRCodeSVG } from "qrcode.react";

import zoniqWordmarkSrc from "../logos/zoniq.png";

/** QR render size in px — larger canvas keeps the center badge readable. */
export const BRANDED_QR_SIZE = 220;
/** Embedded badge size (wordmark + white pad) as a share of QR width. */
export const BRANDED_QR_BADGE_SIZE = 64;

const LOGO_PATH = `${process.env.PUBLIC_URL || ""}/icon-512.png`;

type BadgeImage = { src: string; width: number; height: number };

export type BrandedQrTheme = "dark" | "poster" | "light";

export const BRANDED_QR_COLORS = {
  dark: {
    bgColor: "#0f1016",
    fgColor: "#f5f5f7",
  },
  poster: {
    bgColor: "#ffffff",
    fgColor: "#120f12",
  },
  light: {
    bgColor: "#ffffff",
    fgColor: "#120f12",
  },
} as const;

const ZONIQ_RED = "#c52026";

type QrImageSettings = NonNullable<ComponentProps<typeof QRCodeSVG>["imageSettings"]>;

export type BrandedQrCommonProps = Pick<
  ComponentProps<typeof QRCodeSVG>,
  "id" | "value" | "size" | "level" | "includeMargin" | "bgColor" | "fgColor" | "imageSettings"
>;

function composeLogoBadge(img: HTMLImageElement): string {
  const canvas = document.createElement("canvas");
  canvas.width = BRANDED_QR_BADGE_SIZE;
  canvas.height = BRANDED_QR_BADGE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return LOGO_PATH;

  const radius = 11;
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.roundRect(0, 0, BRANDED_QR_BADGE_SIZE, BRANDED_QR_BADGE_SIZE, radius);
  ctx.fill();

  ctx.strokeStyle = "rgba(18, 15, 18, 0.1)";
  ctx.lineWidth = 1;
  ctx.stroke();

  const inset = 9;
  const iconSize = BRANDED_QR_BADGE_SIZE - inset * 2;
  ctx.drawImage(img, inset, inset, iconSize, iconSize);

  return canvas.toDataURL("image/png");
}

/**
 * Actual Zoniq wordmark (not drawn text) on a white pad, scaled to fit the
 * QR's center at its real aspect ratio — no stretching/cropping. Badge size
 * scales with the QR so the mark stays readable at any render size.
 */
function composeZoniqWordmarkImageBadge(img: HTMLImageElement, qrSize: number): BadgeImage {
  const pad = Math.max(2, Math.round(qrSize * 0.012));
  const contentHeight = Math.max(16, Math.round(qrSize * 0.15));
  const contentWidth = Math.round(contentHeight * (img.naturalWidth / img.naturalHeight));
  const width = contentWidth + pad * 2;
  const height = contentHeight + pad * 2;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { src: zoniqWordmarkSrc, width, height };

  const radius = Math.max(3, Math.round(height * 0.18));
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.roundRect(0, 0, width, height, radius);
  ctx.fill();

  ctx.drawImage(img, pad, pad, contentWidth, contentHeight);

  return { src: canvas.toDataURL("image/png"), width, height };
}

/** White pad + red ZONIQ wordmark for the invite poster QR. */
export function composeZoniqWordmarkBadge(): string {
  const size = BRANDED_QR_BADGE_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return LOGO_PATH;

  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.roundRect(0, 0, size, size, 10);
  ctx.fill();

  ctx.fillStyle = ZONIQ_RED;
  ctx.font = `800 ${Math.round(size * 0.26)}px "Segoe UI", system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("ZONIQ", size / 2, size / 2 + 1);

  return canvas.toDataURL("image/png");
}

export function brandedQrImageSettings(badge: BadgeImage): QrImageSettings {
  return {
    src: badge.src,
    height: badge.height,
    width: badge.width,
    excavate: true,
    crossOrigin: "anonymous",
  };
}

/** Inline logo badge as data URL so SVG downloads and canvas exports include the mark. */
export function useZoniqQrLogoBadge(theme: BrandedQrTheme = "dark", qrSize: number = BRANDED_QR_SIZE): BadgeImage {
  const [badge, setBadge] = useState<BadgeImage>(
    theme === "poster"
      ? { src: composeZoniqWordmarkBadge(), width: BRANDED_QR_BADGE_SIZE, height: BRANDED_QR_BADGE_SIZE }
      : { src: LOGO_PATH, width: BRANDED_QR_BADGE_SIZE, height: BRANDED_QR_BADGE_SIZE },
  );

  useEffect(() => {
    if (theme === "poster") {
      setBadge({ src: composeZoniqWordmarkBadge(), width: BRANDED_QR_BADGE_SIZE, height: BRANDED_QR_BADGE_SIZE });
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";

    if (theme === "light") {
      img.onload = () => {
        try {
          if (!cancelled) setBadge(composeZoniqWordmarkImageBadge(img, qrSize));
        } catch {
          /* keep path fallback */
        }
      };
      img.onerror = () => {
        if (!cancelled) setBadge({ src: zoniqWordmarkSrc, width: BRANDED_QR_BADGE_SIZE, height: BRANDED_QR_BADGE_SIZE });
      };
      img.src = zoniqWordmarkSrc;
      return () => {
        cancelled = true;
      };
    }

    img.onload = () => {
      try {
        if (!cancelled) setBadge({ src: composeLogoBadge(img), width: BRANDED_QR_BADGE_SIZE, height: BRANDED_QR_BADGE_SIZE });
      } catch {
        /* keep path fallback */
      }
    };
    img.onerror = () => {
      if (!cancelled) setBadge({ src: LOGO_PATH, width: BRANDED_QR_BADGE_SIZE, height: BRANDED_QR_BADGE_SIZE });
    };
    img.src = LOGO_PATH;
    return () => {
      cancelled = true;
    };
  }, [theme, qrSize]);

  return badge;
}

export function brandedQrProps(
  id: string,
  value: string,
  badge: BadgeImage,
  theme: BrandedQrTheme = "dark",
  size: number = BRANDED_QR_SIZE,
): BrandedQrCommonProps {
  const colors = BRANDED_QR_COLORS[theme];
  return {
    id,
    value,
    size,
    level: "H",
    includeMargin: true,
    bgColor: colors.bgColor,
    fgColor: colors.fgColor,
    imageSettings: brandedQrImageSettings(badge),
  };
}
